
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  Target,
  TrendingUp,
  Users,
  DollarSign,
  Sparkles,
  Brain,
  Settings,
  Send,
  Eye,
  ChevronRight,
  Star,
  Gift,
  Zap,
  BarChart3,
  UserCheck,
  Clock,
  Award
} from "lucide-react";

interface Recommendation {
  id: string;
  memberId: string;
  memberName: string;
  type: 'offer' | 'upsell' | 'retention' | 'activation';
  title: string;
  description: string;
  confidenceScore: number;
  expectedLTV: number;
  currentLTV: number;
  segment: string;
  recommendedAction: string;
  pointMultiplier?: number;
  offerValue?: number;
  expiryDate: string;
  status: 'pending' | 'sent' | 'accepted' | 'declined' | 'expired';
  metadata: {
    predictedEngagement: number;
    riskScore: number;
    purchaseHistory: string[];
    lastActivity: string;
    preferredCategories: string[];
  };
}

interface Segment {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  avgLTV: number;
  engagementRate: number;
  recommendationStrategy: string;
  pointMultiplier: number;
  activeOffers: number;
}

interface MLModel {
  id: string;
  name: string;
  type: 'ltv_prediction' | 'churn_prediction' | 'next_best_offer' | 'engagement_scoring';
  status: 'active' | 'training' | 'paused';
  accuracy: number;
  lastTrained: string;
  predictionsGenerated: number;
  description: string;
}

const AIRecommendations = () => {
  const { toast } = useToast();
  const [autoRecommendationsEnabled, setAutoRecommendationsEnabled] = useState(true);
  const [ltvThreshold, setLtvThreshold] = useState(1000);
  const [maxOffersPerMember, setMaxOffersPerMember] = useState(3);

  // Sample recommendations
  const [recommendations] = useState<Recommendation[]>([
    {
      id: 'REC-001',
      memberId: 'MEM-123',
      memberName: 'Alice Johnson',
      type: 'upsell',
      title: 'Premium Tier Upgrade',
      description: 'Based on spending patterns, member likely to upgrade to premium tier',
      confidenceScore: 89,
      expectedLTV: 2500,
      currentLTV: 850,
      segment: 'High Value Prospect',
      recommendedAction: 'Offer 2x points for next 30 days',
      pointMultiplier: 2.0,
      offerValue: 150,
      expiryDate: '2025-06-15',
      status: 'pending',
      metadata: {
        predictedEngagement: 78,
        riskScore: 15,
        purchaseHistory: ['Electronics', 'Clothing', 'Books'],
        lastActivity: '2025-05-28',
        preferredCategories: ['Electronics', 'Gadgets']
      }
    },
    {
      id: 'REC-002',
      memberId: 'MEM-456',
      memberName: 'Bob Smith',
      type: 'retention',
      title: 'Re-engagement Campaign',
      description: 'Member showing signs of decreased activity - retention offer recommended',
      confidenceScore: 76,
      expectedLTV: 650,
      currentLTV: 420,
      segment: 'At-Risk',
      recommendedAction: 'Send personalized offer with 20% cashback',
      pointMultiplier: 1.5,
      offerValue: 75,
      expiryDate: '2025-06-10',
      status: 'sent',
      metadata: {
        predictedEngagement: 45,
        riskScore: 65,
        purchaseHistory: ['Groceries', 'Gas'],
        lastActivity: '2025-05-20',
        preferredCategories: ['Groceries', 'Dining']
      }
    },
    {
      id: 'REC-003',
      memberId: 'MEM-789',
      memberName: 'Carol Davis',
      type: 'offer',
      title: 'Category-Specific Bonus',
      description: 'High affinity for beauty products - targeted offer recommended',
      confidenceScore: 92,
      expectedLTV: 1200,
      currentLTV: 680,
      segment: 'Category Enthusiast',
      recommendedAction: '3x points on beauty purchases this month',
      pointMultiplier: 3.0,
      offerValue: 100,
      expiryDate: '2025-06-20',
      status: 'accepted',
      metadata: {
        predictedEngagement: 85,
        riskScore: 20,
        purchaseHistory: ['Beauty', 'Skincare', 'Makeup'],
        lastActivity: '2025-05-29',
        preferredCategories: ['Beauty', 'Personal Care']
      }
    },
    {
      id: 'REC-004',
      memberId: 'MEM-101',
      memberName: 'David Wilson',
      type: 'activation',
      title: 'New Member Onboarding',
      description: 'Recently joined member - activation sequence recommended',
      confidenceScore: 68,
      expectedLTV: 950,
      currentLTV: 0,
      segment: 'New Member',
      recommendedAction: 'Welcome bonus + tutorial points',
      pointMultiplier: 2.5,
      offerValue: 200,
      expiryDate: '2025-06-25',
      status: 'pending',
      metadata: {
        predictedEngagement: 60,
        riskScore: 35,
        purchaseHistory: [],
        lastActivity: '2025-05-29',
        preferredCategories: []
      }
    }
  ]);

  // Sample segments
  const [segments] = useState<Segment[]>([
    {
      id: 'SEG-001',
      name: 'High Value Prospect',
      description: 'Members with high predicted LTV and engagement',
      memberCount: 1250,
      avgLTV: 1850,
      engagementRate: 78,
      recommendationStrategy: 'Premium upgrades and exclusive offers',
      pointMultiplier: 2.0,
      activeOffers: 45
    },
    {
      id: 'SEG-002',
      name: 'At-Risk',
      description: 'Members showing decreased activity patterns',
      memberCount: 680,
      avgLTV: 540,
      engagementRate: 32,
      recommendationStrategy: 'Retention campaigns and win-back offers',
      pointMultiplier: 1.8,
      activeOffers: 28
    },
    {
      id: 'SEG-003',
      name: 'Category Enthusiast',
      description: 'Members with strong affinity for specific categories',
      memberCount: 920,
      avgLTV: 1200,
      engagementRate: 65,
      recommendationStrategy: 'Category-specific bonuses and early access',
      pointMultiplier: 2.5,
      activeOffers: 38
    },
    {
      id: 'SEG-004',
      name: 'New Member',
      description: 'Recently joined members in onboarding phase',
      memberCount: 450,
      avgLTV: 0,
      engagementRate: 55,
      recommendationStrategy: 'Welcome bonuses and educational content',
      pointMultiplier: 2.0,
      activeOffers: 15
    }
  ]);

  // Sample ML models
  const [mlModels] = useState<MLModel[]>([
    {
      id: 'MODEL-LTV',
      name: 'Customer LTV Predictor',
      type: 'ltv_prediction',
      status: 'active',
      accuracy: 87.2,
      lastTrained: '2025-05-28',
      predictionsGenerated: 3420,
      description: 'Predicts customer lifetime value based on behavior patterns'
    },
    {
      id: 'MODEL-CHURN',
      name: 'Churn Risk Classifier',
      type: 'churn_prediction',
      status: 'active',
      accuracy: 82.8,
      lastTrained: '2025-05-27',
      predictionsGenerated: 2150,
      description: 'Identifies members at risk of churning'
    },
    {
      id: 'MODEL-NBO',
      name: 'Next Best Offer Engine',
      type: 'next_best_offer',
      status: 'training',
      accuracy: 79.5,
      lastTrained: '2025-05-26',
      predictionsGenerated: 1890,
      description: 'Recommends optimal offers for each member'
    },
    {
      id: 'MODEL-ENG',
      name: 'Engagement Scorer',
      type: 'engagement_scoring',
      status: 'active',
      accuracy: 91.3,
      lastTrained: '2025-05-28',
      predictionsGenerated: 4560,
      description: 'Scores member engagement likelihood'
    }
  ]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'upsell': return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'retention': return 'bg-orange-100 text-orange-800 hover:bg-orange-100';
      case 'offer': return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'activation': return 'bg-purple-100 text-purple-800 hover:bg-purple-100';
      default: return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
      case 'sent': return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'accepted': return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'declined': return 'bg-red-100 text-red-800 hover:bg-red-100';
      case 'expired': return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
      default: return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  };

  const handleSendRecommendation = (recId: string) => {
    toast({
      title: "Recommendation Sent",
      description: `Personalized offer has been sent to the member.`,
    });
  };

  const handleViewDetails = (recId: string) => {
    toast({
      title: "View Details",
      description: `Opening detailed view for recommendation ${recId}.`,
    });
  };

  const pendingRecommendations = recommendations.filter(rec => rec.status === 'pending').length;
  const avgConfidenceScore = Math.round(recommendations.reduce((sum, rec) => sum + rec.confidenceScore, 0) / recommendations.length);
  const totalExpectedLTV = recommendations.reduce((sum, rec) => sum + rec.expectedLTV, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Recommendations & Offers</h1>
          <p className="text-gray-600">Personalized member recommendations powered by machine learning</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <Brain className="h-3 w-3 mr-1" />
            AI Active
          </Badge>
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Recommendations</p>
                <p className="text-2xl font-bold text-blue-600">{pendingRecommendations}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Confidence</p>
                <p className="text-2xl font-bold text-green-600">{avgConfidenceScore}%</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <Star className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Expected LTV</p>
                <p className="text-2xl font-bold text-purple-600">${(totalExpectedLTV / 1000).toFixed(1)}K</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Segments</p>
                <p className="text-2xl font-bold text-orange-600">{segments.length}</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="recommendations" className="space-y-6">
        <TabsList>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          <TabsTrigger value="segments">Member Segments</TabsTrigger>
          <TabsTrigger value="models">ML Models</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="recommendations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Personalized Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Recommendation</TableHead>
                      <TableHead>Confidence</TableHead>
                      <TableHead>Expected LTV</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recommendations.map((rec) => (
                      <TableRow key={rec.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{rec.memberName}</div>
                            <div className="text-sm text-gray-500">{rec.memberId}</div>
                            <Badge variant="outline" className="mt-1 text-xs">
                              {rec.segment}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getTypeColor(rec.type)}>
                            {rec.type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{rec.title}</div>
                            <div className="text-sm text-gray-500 max-w-[200px] truncate">
                              {rec.description}
                            </div>
                            <div className="text-sm text-blue-600 mt-1">
                              {rec.recommendedAction}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full bg-blue-500"
                                style={{ width: `${rec.confidenceScore}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{rec.confidenceScore}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">${rec.expectedLTV}</div>
                            <div className="text-sm text-gray-500">
                              Current: ${rec.currentLTV}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getStatusColor(rec.status)}>
                            {rec.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(rec.id)}
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                            {rec.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSendRecommendation(rec.id)}
                              >
                                <Send className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="segments" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {segments.map((segment) => (
              <Card key={segment.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{segment.name}</span>
                    <Badge variant="outline">{segment.memberCount} members</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">{segment.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg LTV</p>
                      <p className="text-lg font-bold text-green-600">${segment.avgLTV}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Engagement</p>
                      <p className="text-lg font-bold text-blue-600">{segment.engagementRate}%</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">Strategy</p>
                    <p className="text-sm text-gray-800">{segment.recommendationStrategy}</p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-orange-500" />
                      <span className="text-sm">{segment.pointMultiplier}x multiplier</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Gift className="h-4 w-4 text-purple-500" />
                      <span className="text-sm">{segment.activeOffers} active offers</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="models" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mlModels.map((model) => (
              <Card key={model.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{model.name}</span>
                    <Badge variant="outline" className={
                      model.status === 'active' ? 'bg-green-100 text-green-800' :
                      model.status === 'training' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }>
                      {model.status}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">{model.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Accuracy</p>
                      <p className="text-lg font-bold text-green-600">{model.accuracy}%</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Predictions</p>
                      <p className="text-lg font-bold text-blue-600">{model.predictionsGenerated.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">Last trained: {model.lastTrained}</span>
                    </div>
                    <Button variant="outline" size="sm">
                      {model.status === 'training' ? 'View Progress' : 'Retrain'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Recommendation Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Auto-generate Recommendations</Label>
                  <p className="text-xs text-gray-500">Automatically create personalized recommendations</p>
                </div>
                <Switch
                  checked={autoRecommendationsEnabled}
                  onCheckedChange={setAutoRecommendationsEnabled}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">LTV Threshold</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={ltvThreshold}
                    onChange={(e) => setLtvThreshold(Number(e.target.value))}
                    className="w-32"
                  />
                  <span className="text-sm text-gray-500">$ minimum expected LTV</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Max Offers per Member</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={maxOffersPerMember}
                    onChange={(e) => setMaxOffersPerMember(Number(e.target.value))}
                    className="w-20"
                    min="1"
                    max="10"
                  />
                  <span className="text-sm text-gray-500">offers per month</span>
                </div>
              </div>

              <Button className="w-full">
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIRecommendations;
